import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Receipt, TrendingUp, Pencil, Link2, Store, CheckCircle2, Calendar, Search, Filter, ArrowUp, ArrowDown, ArrowUpDown, AlertCircle, UserPlus, Users } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import SortPopover from "@/components/SortPopover";
import { useTranslation, Trans } from "react-i18next";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";

const PAYMENT_KEYS = ["card", "cash", "bank_transfer", "other"];

const INVOICE_SORT_FIELDS = [
  { key: "paid_at", label: "invoices.sort.paid_at" },
  { key: "total", label: "invoices.sort.total" },
  { key: "created_at", label: "invoices.sort.created_at" },
];

const eur = (n) => fmtMoney(n ?? 0);

// Robust select-all on focus (works with mouse click too — mouseup fires AFTER
// browser positions caret, so we re-select then). Prevents "append to existing
// value" (sovraprezzo) UX where user types into an already-filled number input.
const selectAllHandlers = {
  onFocus: (e) => {
    const el = e.currentTarget;
    // schedule so it runs after mouseup positions the caret
    setTimeout(() => { try { el.select(); } catch (_) {} }, 0);
  },
  onMouseUp: (e) => {
    // second layer: prevent default which normally cancels our select
    e.preventDefault();
    e.currentTarget.select();
  },
};

export default function Invoices() {
  const { t } = useTranslation();
  const payLabel = (k) => (PAYMENT_KEYS.includes(k) ? t(`invoices.payments.${k}`) : k);
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [editing, setEditing] = useState(null); // invoice object for edit, {} for new (cassa), { __linkAppt: true } for new-appt mode
  const [saving, setSaving] = useState(false);

  // ?unassigned=1 → filtro speciale per riassegnare gli incassi senza collaboratore
  const location = useLocation();
  const navigate = useNavigate();
  const unassignedFilter = new URLSearchParams(location.search).get("unassigned") === "1";

  const load = async () => {
    const [i, c, s, p, a, st] = await Promise.all([
      api.get("/invoices"),
      api.get("/clients"),
      api.get("/services"),
      api.get("/products").catch(() => ({ data: [] })),
      // Wide window to link any invoice with any recent/future appt
      api.get("/appointments?date_from=2024-01-01T00:00:00&date_to=2099-12-31T23:59:59").catch(() => ({ data: [] })),
      api.get("/staff").catch(() => ({ data: [] })),
    ]);
    setItems(i.data); setClients(c.data); setServices(s.data); setProducts(p.data); setAppointments(a.data); setStaff(st.data || []);
  };
  useEffect(() => { load(); }, []);

  // Assegna staff a incasso singolo (usato dal filtro unassigned)
  const assignStaff = async (invId, staffId) => {
    try {
      await api.patch(`/invoices/${invId}`, { staff_id: staffId });
      toast.success(t("invoices.staffAssigned"));
      load();
    } catch (e) { toast.error(t("invoices.assignError")); }
  };

  // Staff coinvolti in un incasso (multi via appointment.service_items)
  const staffOnInvoice = (inv) => {
    const appt = apptOf(inv.appointment_id);
    if (!appt) return inv.staff_id ? [inv.staff_id] : [];
    const items = appt.service_items || [];
    const sids = new Set();
    for (const it of items) if (it.staff_id) sids.add(it.staff_id);
    if (sids.size === 0 && (appt.staff_id || inv.staff_id)) sids.add(appt.staff_id || inv.staff_id);
    return Array.from(sids);
  };
  const staffOf = (sid) => staff.find((s) => s.id === sid);

  const clientOf = (id) => clients.find((c) => c.id === id);
  const apptOf = (id) => appointments.find((a) => a.id === id);
  const svcName = (id) => services.find((s) => s.id === id)?.name || "—";

  // --- Filtri e ordinamento (stile Contatti/Pipeline) ---------------------
  const [showFilters, setShowFilters] = useState(false);
  const [query, setQuery] = useState("");
  const [sortField, setSortField] = useState("paid_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filters, setFilters] = useState({
    client_id: "",
    staff_id: "",
    payment_method: "",
    amount_min: "",
    amount_max: "",
    date_since: "",
    date_until: "",
  });
  const resetFilters = () => setFilters({
    client_id: "", staff_id: "", payment_method: "",
    amount_min: "", amount_max: "", date_since: "", date_until: "",
  });
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.client_id) n++;
    if (filters.staff_id) n++;
    if (filters.payment_method) n++;
    if (filters.amount_min || filters.amount_max) n++;
    if (filters.date_since || filters.date_until) n++;
    return n;
  }, [filters]);

  // --- Lista filtrata + ordinata (usata sia da tbody che dal totale) -----
  const filtered = useMemo(() => {
    let list = items.slice();
    if (unassignedFilter) list = list.filter((inv) => staffOnInvoice(inv).length === 0);
    if (filters.client_id) list = list.filter((inv) => inv.client_id === filters.client_id);
    if (filters.staff_id) list = list.filter((inv) => staffOnInvoice(inv).includes(filters.staff_id));
    if (filters.payment_method) list = list.filter((inv) => inv.payment_method === filters.payment_method);
    const amMin = parseFloat(filters.amount_min || "");
    const amMax = parseFloat(filters.amount_max || "");
    if (!Number.isNaN(amMin)) list = list.filter((inv) => (inv.total || 0) >= amMin);
    if (!Number.isNaN(amMax)) list = list.filter((inv) => (inv.total || 0) <= amMax);
    if (filters.date_since) list = list.filter((inv) => (inv.paid_at || inv.created_at || "").slice(0, 10) >= filters.date_since);
    if (filters.date_until) list = list.filter((inv) => (inv.paid_at || inv.created_at || "").slice(0, 10) <= filters.date_until);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((inv) => {
      const cn = (clientOf(inv.client_id)?.name || "").toLowerCase();
      const nt = (inv.notes || "").toLowerCase();
      const num = (inv.number || "").toLowerCase();
      return cn.includes(q) || nt.includes(q) || num.includes(q);
    });
    list.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const av = a[sortField]; const bv = b[sortField];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av || "").localeCompare(String(bv || "")) * dir;
    });
    return list;
  }, [items, filters, query, sortField, sortDir, unassignedFilter, clients, appointments]); // eslint-disable-line

  // Totali dei risultati filtrati — mostrati nel piede della tabella
  const filteredTotals = useMemo(() => {
    const count = filtered.length;
    const sum = filtered.reduce((a, i) => a + (i.total || 0), 0);
    const byPayment = filtered.reduce((acc, i) => {
      const k = i.payment_method || "other";
      acc[k] = (acc[k] || 0) + (i.total || 0);
      return acc;
    }, {});
    const avg = count > 0 ? sum / count : 0;
    return { count, sum, avg, byPayment };
  }, [filtered]);

  const remove = async (id) => {
    if (!window.confirm(t("invoices.deleteConfirm"))) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(t("invoices.deleted"));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("common.error")); }
  };

  const monthRevenue = useMemo(() => {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return items.filter((i) => i.paid_at?.startsWith(ym) || i.created_at?.startsWith(ym)).reduce((a, i) => a + (i.total || 0), 0);
  }, [items]);

  return (
    <div data-testid="invoices-page" className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight inline-flex items-center gap-3">
            <Receipt className="h-7 w-7 text-[color:var(--sp-brand)]" />
            {t("invoices.title")}
          </h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2">{t("invoices.subtitle")}</p>
        </div>
        <div className="inline-flex items-center gap-2">
          <button data-testid="invoices-new-btn" onClick={() => setEditing({})} className="sp-btn-ghost inline-flex items-center gap-2 text-sm"><Plus className="h-4 w-4" /> {t("invoices.newCash")}</button>
          <button data-testid="invoices-new-appt-btn" onClick={() => setEditing({ __linkAppt: true })} className="sp-btn-primary inline-flex items-center gap-2"><Plus className="h-4 w-4" /> {t("invoices.newAppt")}</button>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="sp-card p-5 sp-anim">
          <div className="flex items-center justify-between"><span className="label-eyebrow">{t("invoices.kpiTotal")}</span><Receipt className="h-4 w-4 text-[color:var(--sp-brand)]" /></div>
          <div className="font-display text-3xl font-black mt-3">{items.length}</div>
        </div>
        <div className="sp-card p-5 sp-anim">
          <span className="label-eyebrow">{t("invoices.kpiMonth")}</span>
          <div className="font-display text-3xl font-black mt-3 text-gradient">{eur(monthRevenue)}</div>
        </div>
        <div className="sp-card p-5 sp-anim">
          <div className="flex items-center justify-between"><span className="label-eyebrow">{t("invoices.kpiAll")}</span><TrendingUp className="h-4 w-4 text-[color:var(--sp-brand)]" /></div>
          <div className="font-display text-3xl font-black mt-3">{eur(items.reduce((a, i) => a + (i.total || 0), 0))}</div>
        </div>
      </section>

      {/* Search + Sort + Filter bar (stile Contatti/Pipeline) */}
      <div className="sp-card p-3 flex flex-wrap gap-2 items-center" data-testid="invoices-toolbar">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
          <input data-testid="invoices-search-input"
                 placeholder={t("invoices.searchPlaceholder")}
                 className="sp-input !pl-10 !py-1.5 text-sm"
                 value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <SortPopover
          fields={INVOICE_SORT_FIELDS}
          field={sortField} dir={sortDir}
          onChange={(f, d) => { setSortField(f); setSortDir(d); }}
          testid="invoices-sort"
          defaultField="paid_at" defaultDir="desc" />
        <button onClick={() => setShowFilters(true)} data-testid="invoices-filter-btn"
                className="sp-btn-ghost inline-flex items-center gap-2 text-sm relative">
          <Filter className="h-4 w-4" /> {t("invoices.filters")}
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-[color:var(--sp-brand)] text-white text-[9px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {unassignedFilter && (
        <div className="sp-card p-4 bg-amber-50/60 border-amber-200 flex items-center gap-3 flex-wrap" data-testid="unassigned-filter-banner">
          <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-bold text-amber-900">{t("invoices.unassignedTitle")}</div>
            <div className="text-xs text-amber-800 mt-0.5">
              {t("invoices.unassignedHint")}
            </div>
          </div>
          <button onClick={() => navigate("/app/invoices")} data-testid="unassigned-clear-btn"
                  className="text-xs font-bold text-amber-900 hover:underline inline-flex items-center gap-1">
            <X className="h-3 w-3" /> {t("invoices.clearFilter")}
          </button>
        </div>
      )}
      <div className="sp-card overflow-hidden" data-testid="invoices-list">
        {filtered.length === 0 && items.length === 0 ? (
          <div className="p-12 text-center text-[color:var(--sp-text-secondary)]">
            <Receipt className="h-8 w-8 mx-auto text-[color:var(--sp-border-strong)] mb-3" />
            {t("invoices.empty")}
          </div>
        ) : (
          <div className="sp-table-wrap">
          <table className="w-full">
            <thead className="bg-[color:var(--sp-subtle)] text-left">
              <tr className="label-eyebrow">
                <th className="p-4">{t("invoices.cols.origin")}</th>
                <th className="p-4">{t("invoices.cols.client")}</th>
                <th className="p-4">{t("invoices.cols.staff")}</th>
                <th className="p-4">{t("invoices.cols.date")}</th>
                <th className="p-4">{t("invoices.cols.composition")}</th>
                <th className="p-4">{t("invoices.cols.method")}</th>
                <th className="p-4 text-right">{t("invoices.cols.amount")}</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv, i) => {
                const appt = apptOf(inv.appointment_id);
                const lineKinds = (inv.lines || []).reduce((acc, l) => {
                  const k = l.kind || (l.service_id ? "service" : (l.product_id ? "product" : "other"));
                  acc[k] = (acc[k] || 0) + 1;
                  return acc;
                }, {});
                return (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className={`${i % 2 ? "bg-[color:var(--sp-bg)]" : ""} hover:bg-[color:var(--sp-subtle)]/60 transition-colors`}>
                    <td className="p-4">
                      {appt ? (
                        <Link to={`/app/agenda?date=${appt.start?.slice(0,10)}&edit=${appt.id}`}
                              data-testid={`invoice-appt-link-${inv.id}`}
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold text-[color:var(--sp-brand)] bg-[color:var(--sp-brand)]/10 hover:bg-[color:var(--sp-brand)]/20 transition-colors">
                          <Link2 className="h-3 w-3" />
                          <span>{fmtDateTime(appt.start, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                        </Link>
                      ) : inv.appointment_id ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold text-[color:var(--sp-text-muted)] bg-[color:var(--sp-subtle)]" title={t("invoices.apptDeletedTitle")}>
                          <Link2 className="h-3 w-3" /> {t("invoices.apptDeleted")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold text-purple-700 bg-purple-100">
                          <Store className="h-3 w-3" /> {t("invoices.freeSale")}
                        </span>
                      )}
                    </td>
                    <td className="p-4 font-bold text-sm">
                      {inv.client_id && clientOf(inv.client_id) ? (
                        <Link to={`/app/clients/${inv.client_id}`}
                              data-testid={`invoice-client-link-${inv.id}`}
                              className="hover:text-[color:var(--sp-brand)] hover:underline transition-colors">
                          {clientOf(inv.client_id).name}
                        </Link>
                      ) : (
                        <em className="text-[color:var(--sp-text-muted)] font-normal">{t("invoices.anonymous")}</em>
                      )}
                    </td>
                    <td className="p-4">
                      {(() => {
                        const sids = staffOnInvoice(inv);
                        if (sids.length === 0) {
                          return (
                            <div className="inline-flex items-center gap-1">
                              <select data-testid={`invoice-assign-staff-${inv.id}`}
                                      defaultValue=""
                                      onChange={(e) => { if (e.target.value) assignStaff(inv.id, e.target.value); }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-[10px] px-2 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300 font-bold cursor-pointer hover:bg-amber-200 transition">
                                <option value="">{t("invoices.assignShort")}</option>
                                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                              </select>
                            </div>
                          );
                        }
                        return (
                          <div className="flex -space-x-1.5" title={sids.map((sid) => staffOf(sid)?.name || "?").join(", ")}>
                            {sids.slice(0, 3).map((sid) => {
                              const s = staffOf(sid);
                              return (
                                <span key={sid} className="h-6 w-6 rounded-full flex items-center justify-center text-white text-[10px] font-black ring-2 ring-white"
                                      style={{ background: s?.color || "#94A3B8" }}>{(s?.name || "?").charAt(0).toUpperCase()}</span>
                              );
                            })}
                            {sids.length > 3 && (
                              <span className="h-6 w-6 rounded-full bg-slate-200 text-[10px] font-black flex items-center justify-center ring-2 ring-white">+{sids.length - 3}</span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-4 text-sm text-[color:var(--sp-text-secondary)]">{fmtDate(inv.paid_at || inv.created_at)}</td>
                    <td className="p-4 text-xs">
                      <div className="flex flex-wrap gap-1">
                        {lineKinds.service > 0 && <span className="px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 font-bold">{lineKinds.service} {t("invoices.serviceShort")}</span>}
                        {lineKinds.product > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">{lineKinds.product} {t("invoices.productShort")}</span>}
                        {lineKinds.other > 0 && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold">{lineKinds.other} {t("invoices.extraShort")}</span>}
                      </div>
                    </td>
                    <td className="p-4 text-sm">
                      <span className="px-2 py-1 rounded-full bg-[color:var(--sp-subtle)] text-xs font-semibold">{payLabel(inv.payment_method)}</span>
                    </td>
                    <td className="p-4 text-right font-display font-black text-[color:var(--sp-brand)]">{eur(inv.total)}</td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button data-testid={`invoice-edit-${inv.id}`} onClick={() => setEditing(inv)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-brand)]" title={t("common.edit")}><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`invoice-delete-${inv.id}`} onClick={() => remove(inv.id)} className="p-1 ml-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-danger)]" title={t("common.delete")}><X className="h-4 w-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filtered.length > 0 && (
              <tfoot data-testid="invoices-table-totals">
                <tr className="bg-[color:var(--sp-subtle)] border-t-2 border-[color:var(--sp-border-strong)]">
                  <td className="p-4 label-eyebrow" colSpan={2}>
                    {t("invoices.filteredTotal")}
                  </td>
                  <td className="p-4 text-xs font-bold text-[color:var(--sp-text-secondary)]" colSpan={2}>
                    {t("invoices.count", { count: filteredTotals.count })} · {t("invoices.avgTicket")} <span className="text-[color:var(--sp-brand)]">{eur(filteredTotals.avg)}</span>
                  </td>
                  <td className="p-4" colSpan={2}>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(filteredTotals.byPayment).sort((a,b) => b[1] - a[1]).map(([k, v]) => (
                        <span key={k} className="px-2 py-0.5 rounded-full bg-white border border-[color:var(--sp-border)] text-[10px] font-bold text-[color:var(--sp-text-secondary)]">
                          {payLabel(k)}: {eur(v)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-4 text-right font-display font-black text-lg text-[color:var(--sp-brand)] tabular-nums" data-testid="invoices-totals-sum">
                    {eur(filteredTotals.sum)}
                  </td>
                  <td className="p-4"></td>
                </tr>
              </tfoot>
            )}
          </table>
          </div>
        )}
      </div>

      {editing && (
        <InvoiceModal
          invoice={editing.id ? editing : null}
          linkAppt={!!editing.__linkAppt}
          clients={clients}
          services={services}
          products={products}
          appointments={appointments}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {showFilters && (
        <InvoiceFiltersDrawer
          filters={filters} setFilters={setFilters}
          clients={clients} staff={staff}
          onClose={() => setShowFilters(false)}
          onReset={resetFilters}
        />
      )}
    </div>
  );
}

function InvoiceFiltersDrawer({ filters, setFilters, clients, staff, onClose, onReset }) {
  const { t } = useTranslation();
  const [f, setF] = useState(filters);
  const apply = () => { setFilters(f); onClose(); };
  const reset = () => {
    const empty = { client_id: "", staff_id: "", payment_method: "",
      amount_min: "", amount_max: "", date_since: "", date_until: "" };
    setF(empty); setFilters(empty); onReset && onReset();
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose}
         data-testid="invoices-filters-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-black">{t("invoices.filters")}</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("invoices.cols.client")}</label>
            <select value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })}
              className="sp-input mt-1" data-testid="filter-client">
              <option value="">{t("invoices.all")}</option>
              {clients.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("invoices.filterStaff")}</label>
            <select value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })}
              className="sp-input mt-1" data-testid="filter-staff">
              <option value="">{t("invoices.all")}</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("invoices.filterPayment")}</label>
            <select value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })}
              className="sp-input mt-1" data-testid="filter-payment">
              <option value="">{t("invoices.all")}</option>
              {PAYMENT_KEYS.map((k) => (
                <option key={k} value={k}>{t(`invoices.payments.${k}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("invoices.filterAmount")}</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <input type="number" step="0.01" min="0" placeholder="min"
                value={f.amount_min} onChange={(e) => setF({ ...f, amount_min: e.target.value })}
                className="sp-input" data-testid="filter-amount-min" />
              <input type="number" step="0.01" min="0" placeholder="max"
                value={f.amount_max} onChange={(e) => setF({ ...f, amount_max: e.target.value })}
                className="sp-input" data-testid="filter-amount-max" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("invoices.filterDate")}</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <input type="date" value={f.date_since} onChange={(e) => setF({ ...f, date_since: e.target.value })}
                className="sp-input" data-testid="filter-date-since" />
              <input type="date" value={f.date_until} onChange={(e) => setF({ ...f, date_until: e.target.value })}
                className="sp-input" data-testid="filter-date-until" />
            </div>
          </div>
        </div>
        <div className="flex justify-between mt-6">
          <button onClick={reset} className="sp-btn-ghost text-sm" data-testid="filters-reset">{t("common.reset")}</button>
          <button onClick={apply} className="sp-btn-primary" data-testid="filters-apply">{t("invoices.apply")}</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceModal({ invoice, linkAppt = false, clients, services, products, appointments, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!invoice;
  const today = new Date().toISOString().slice(0, 10);
  const [clientId, setClientId] = useState(invoice?.client_id || "");
  const [appointmentId, setAppointmentId] = useState(invoice?.appointment_id || "");
  const [payment, setPayment] = useState(invoice?.payment_method || "cash");
  const [notes, setNotes] = useState(invoice?.notes || "");
  const [saving, setSaving] = useState(false);
  // Manual paid_at date (defaults to today for new / existing paid_at)
  const [paidAt, setPaidAt] = useState(() => (invoice?.paid_at || invoice?.created_at || today).slice(0, 10));
  // Toggle: if linkAppt or invoice already had an appointment, start in "linked" mode
  const [mode, setMode] = useState(() => (linkAppt || invoice?.appointment_id) ? "appt" : "date");
  const [lines, setLines] = useState(() => {
    if (invoice?.lines?.length) {
      return invoice.lines.map((l) => ({
        kind: l.kind || (l.service_id ? "service" : (l.product_id ? "product" : "other")),
        description: l.description || "",
        quantity: l.quantity ?? 1,
        unit_price: l.unit_price ?? 0,
        service_id: l.service_id || null,
        product_id: l.product_id || null,
      }));
    }
    return [{ kind: "service", description: "", quantity: 1, unit_price: 0 }];
  });

  const total = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  const updateLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const addLine = (kind) => setLines([...lines, { kind, description: "", quantity: 1, unit_price: 0 }]);
  const addServiceLine = (svc) => setLines([...lines, { kind: "service", description: svc.name, quantity: 1, unit_price: svc.price, service_id: svc.id }]);
  const addProductLine = (prd) => setLines([...lines, { kind: "product", description: prd.name, quantity: 1, unit_price: (prd.retail_price ?? prd.price ?? prd.unit_cost ?? 0), product_id: prd.id }]);

  const onProductPick = (i, pid) => {
    const prd = products.find((p) => p.id === pid);
    if (!prd) { updateLine(i, { product_id: null }); return; }
    // Prefer retail_price (Prezzo di rivendita) then legacy .price then unit_cost
    const defaultPrice = prd.retail_price ?? prd.price ?? prd.unit_cost ?? 0;
    updateLine(i, { product_id: prd.id, description: prd.name, unit_price: defaultPrice });
  };
  const onServicePick = (i, sid) => {
    const svc = services.find((s) => s.id === sid);
    if (!svc) { updateLine(i, { service_id: null }); return; }
    updateLine(i, { service_id: svc.id, description: svc.name, unit_price: svc.price });
  };

  // Restrict appointment dropdown to those of the selected client (if any)
  const availableAppointments = useMemo(() => {
    const list = clientId ? appointments.filter((a) => a.client_id === clientId) : appointments;
    return [...list].sort((a, b) => new Date(b.start) - new Date(a.start)).slice(0, 100);
  }, [appointments, clientId]);

  const submit = async (e) => {
    e.preventDefault();
    const cleanLines = lines.filter((l) => (Number(l.quantity) || 0) > 0 && (l.description || l.service_id || l.product_id));
    if (cleanLines.length === 0) { toast.error(t("invoices.modal.addLine")); return; }
    // Warn on negative stock (for NEW quantities vs current, minus what this invoice already deducted)
    for (const l of cleanLines) {
      if (l.kind === "product" && l.product_id) {
        const prd = products.find((p) => p.id === l.product_id);
        if (!prd) continue;
        const originalQtyThisInvoice = invoice?.lines?.filter((x) => x.product_id === l.product_id).reduce((s, x) => s + Number(x.quantity || 0), 0) || 0;
        const availableAfterRevert = (prd.quantity ?? 0) + originalQtyThisInvoice;
        if (availableAfterRevert < Number(l.quantity)) {
          if (!window.confirm(t("invoices.modal.stockWarn", { available: availableAfterRevert, name: prd.name, qty: l.quantity }))) return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId || null,
        appointment_id: appointmentId || null,
        lines: cleanLines.map((l) => ({
          description: l.description || null,
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          service_id: l.service_id || null,
          product_id: l.product_id || null,
          kind: l.kind || "other",
        })),
        payment_method: payment,
        tax_rate: 0,
        notes: notes || null,
        // Always send paid_at as ISO derived from the date input (noon UTC to be timezone-safe)
        paid_at: paidAt ? `${paidAt}T12:00:00Z` : null,
      };
      if (isEdit) {
        await api.patch(`/invoices/${invoice.id}`, payload);
        toast.success(t("invoices.modal.updated"));
      } else {
        await api.post("/invoices", payload);
        toast.success(t("invoices.modal.registered", { amount: eur(total) }));
      }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || t("common.error")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="sp-card w-full max-w-3xl p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="label-eyebrow">{t("invoices.modal.eyebrow")}</span>
            <h3 className="font-display text-2xl font-black mt-1 inline-flex items-center gap-2">
              <Receipt className="h-5 w-5 text-[color:var(--sp-brand)]" />
              {isEdit ? t("invoices.modal.editTitle", { number: invoice.number || "" }) : t("invoices.modal.newTitle")}
            </h3>
          </div>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-bold">{t("invoices.cols.client")} <span className="text-[color:var(--sp-text-muted)] font-normal">{t("invoices.modal.clientOptional")}</span></label>
              <div className="mt-1">
                <SearchableSelect
                  testId="invoice-client-select"
                  value={clientId}
                  onChange={(v) => { setClientId(v); setAppointmentId(""); }}
                  options={clients.map((c) => ({ id: c.id, label: c.name, sub: c.phone || c.email || "" }))}
                  placeholder={t("invoices.modal.anonymousSale")}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-bold inline-flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {t("invoices.modal.reference")}</label>
                <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-0.5 text-[10px]">
                  <button type="button" data-testid="invoice-mode-date" onClick={() => { setMode("date"); setAppointmentId(""); }} className={`px-2 py-0.5 rounded-full font-bold ${mode === "date" ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>{t("invoices.modal.date")}</button>
                  <button type="button" data-testid="invoice-mode-appt" onClick={() => setMode("appt")} className={`px-2 py-0.5 rounded-full font-bold ${mode === "appt" ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>{t("invoices.modal.appointment")}</button>
                </div>
              </div>
              {mode === "date" ? (
                <>
                  <input data-testid="invoice-paid-at" type="date" className="sp-input mt-1" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
                  <div className="mt-1 text-[10px] text-[color:var(--sp-text-muted)]">{t("invoices.modal.freeSaleHint")}</div>
                </>
              ) : (
                <>
                  <div className="mt-1">
                    <SearchableSelect
                      testId="invoice-appt-select"
                      value={appointmentId}
                      onChange={setAppointmentId}
                      options={availableAppointments.map((a) => {
                        const cn = clients.find((c) => c.id === a.client_id)?.name || "—";
                        const dt = fmtDateTime(a.start, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
                        return { id: a.id, label: `${dt} · ${cn}`, sub: a.status === "completed" ? t("invoices.modal.completedShort") : "" };
                      })}
                      placeholder={t("invoices.modal.selectAppt")}
                    />
                  </div>
                  {appointmentId && <div className="mt-1 text-[10px] text-[color:var(--sp-text-muted)]"><Trans i18nKey="invoices.modal.apptCompletedHint" components={{ 1: <strong /> }} /></div>}
                </>
              )}
            </div>
          </div>

          {/* Lines table */}
          <div className="rounded-lg border border-[color:var(--sp-border)] overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[color:var(--sp-subtle)] label-eyebrow text-[10px]">
              <div className="col-span-2">{t("invoices.modal.type")}</div>
              <div className="col-span-5">{t("invoices.modal.descOrigin")}</div>
              <div className="col-span-1 text-right">{t("invoices.modal.qty")}</div>
              <div className="col-span-2 text-right">{t("invoices.modal.price")}</div>
              <div className="col-span-1 text-right">{t("invoices.modal.total")}</div>
              <div className="col-span-1"></div>
            </div>
            {lines.map((l, i) => {
              const lineTotal = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
              const isProduct = l.kind === "product";
              const isService = l.kind === "service";
              const prd = isProduct && l.product_id ? products.find((p) => p.id === l.product_id) : null;
              return (
                <div key={i} data-testid={`invoice-line-${i}`} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-[color:var(--sp-border)] items-center">
                  <div className="col-span-2">
                    <select value={l.kind || "service"} onChange={(e) => updateLine(i, { kind: e.target.value, product_id: null, service_id: null })} className="sp-input !py-1 text-xs">
                      <option value="service">{t("invoices.modal.service")}</option>
                      <option value="product">{t("invoices.modal.product")}</option>
                      <option value="other">{t("invoices.modal.other")}</option>
                    </select>
                  </div>
                  <div className="col-span-5">
                    {isProduct && products.length > 0 ? (
                      <SearchableSelect
                        testId={`invoice-line-product-${i}`}
                        value={l.product_id || ""}
                        onChange={(v) => onProductPick(i, v)}
                        options={products.map((p) => ({
                          id: p.id,
                          label: p.name,
                          sub: `${t("invoices.modal.stock")}: ${p.quantity ?? 0}${p.min_stock && (p.quantity ?? 0) <= p.min_stock ? " ⚠" : ""}`,
                        }))}
                        placeholder={t("invoices.modal.chooseStock")}
                      />
                    ) : isService && services.length > 0 ? (
                      <SearchableSelect
                        testId={`invoice-line-service-${i}`}
                        value={l.service_id || ""}
                        onChange={(v) => onServicePick(i, v)}
                        options={services.map((s) => ({
                          id: s.id,
                          label: s.name,
                          sub: fmtMoney(s.price),
                          color: s.color,
                        }))}
                        placeholder={t("invoices.modal.chooseCatalog")}
                      />
                    ) : (
                      <input data-testid={`invoice-line-desc-${i}`} className="sp-input !py-1 text-xs" placeholder={t("invoices.modal.description")} value={l.description || ""} onChange={(e) => updateLine(i, { description: e.target.value })} />
                    )}
                    {prd && (
                      <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-0.5">
                        {t("invoices.modal.currentStock")} <strong>{prd.quantity ?? 0}</strong>
                      </div>
                    )}
                  </div>
                  <div className="col-span-1">
                    <input data-testid={`invoice-line-qty-${i}`} type="number" step="1" min="0" {...selectAllHandlers} className="sp-input !py-1 text-xs text-right" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <input data-testid={`invoice-line-price-${i}`} type="number" step="0.01" min="0" {...selectAllHandlers} className="sp-input !py-1 text-xs text-right" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} />
                  </div>
                  <div className="col-span-1 text-right text-xs font-bold">{fmtMoney(lineTotal)}</div>
                  <div className="col-span-1 text-right">
                    <button type="button" onClick={() => removeLine(i)} className="p-1 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]"><X className="h-3 w-3" /></button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <button type="button" data-testid="invoice-add-service" onClick={() => addLine("service")} className="sp-btn-ghost text-xs inline-flex items-center gap-1"><Plus className="h-3 w-3" /> {t("invoices.modal.service")}</button>
            <button type="button" data-testid="invoice-add-product" onClick={() => addLine("product")} className="sp-btn-ghost text-xs inline-flex items-center gap-1"><Plus className="h-3 w-3" /> {t("invoices.modal.product")}</button>
            <button type="button" data-testid="invoice-add-other" onClick={() => addLine("other")} className="sp-btn-ghost text-xs inline-flex items-center gap-1"><Plus className="h-3 w-3" /> {t("invoices.modal.freeLine")}</button>
            {services.length > 0 && (
              <select onChange={(e) => { const s = services.find((x) => x.id === e.target.value); if (s) { addServiceLine(s); e.target.value = ""; } }} value="" className="sp-input !py-1 text-xs ml-auto w-auto">
                <option value="">{t("invoices.modal.serviceFromCatalog")}</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name} · {fmtMoney(s.price)}</option>)}
              </select>
            )}
            {products.length > 0 && (
              <select onChange={(e) => { const p = products.find((x) => x.id === e.target.value); if (p) { addProductLine(p); e.target.value = ""; } }} value="" className="sp-input !py-1 text-xs w-auto">
                <option value="">{t("invoices.modal.productFromStock")}</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {fmtMoney(p.price ?? p.retail_price ?? 0)} · {t("invoices.modal.stock")} {p.quantity ?? 0}</option>)}
              </select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-bold">{t("invoices.modal.paymentMethod")}</label>
              <select data-testid="invoice-payment-select" className="sp-input mt-1" value={payment} onChange={(e) => setPayment(e.target.value)}>
                <option value="cash">{t("invoices.payments.cash")}</option>
                <option value="card">{t("invoices.payments.card")}</option>
                <option value="bank_transfer">{t("invoices.payments.bank_transfer")}</option>
                <option value="other">{t("invoices.payments.other")}</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-bold">{t("invoices.modal.notes")}</label>
              <input data-testid="invoice-notes" className="sp-input mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-[color:var(--sp-brand)]/10 border border-[color:var(--sp-brand)]/30">
            <div>
              <span className="text-sm font-bold text-[color:var(--sp-brand)] uppercase tracking-widest">{t("invoices.modal.totalRevenue")}</span>
              {lines.some((l) => l.kind === "product" && l.product_id) && (
                <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">{t("invoices.modal.stockHint")}</div>
              )}
            </div>
            <span data-testid="invoice-total" className="font-display text-3xl font-black text-[color:var(--sp-brand)]">{eur(total)}</span>
          </div>

          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="sp-btn-ghost flex-1">{t("common.cancel")}</button>
            <button data-testid="invoice-submit-btn" disabled={saving} className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2 rounded-full text-sm font-bold bg-[color:var(--sp-brand)] text-white hover:opacity-90 disabled:opacity-50">
              <CheckCircle2 className="h-4 w-4" /> {saving ? t("invoices.modal.saving") : (isEdit ? t("invoices.modal.saveChanges") : t("invoices.modal.register"))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function KpiTile() { return null; /* deprecated — spostato nel Dashboard */ }
